import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscriptionService';
import type { SubscriptionPlan } from '../types/subscription.types';
import { precioPromocional } from '../config/bundleConfig';

export interface PromoPlan {
  id: string;
  nombre: string;
  /** Precio de la promoción: lo que se cobra. */
  precio: number;
  /**
   * Precio de lista del plan, el que se muestra tachado.
   */
  precioRegular: number | null;
  /** Primera característica del plan, para destacar en la tarjeta. */
  destacado: string | null;
}

/**
 * Último día de la campaña mientras el catálogo no lo declare en
 * `limits.descuento_hasta`.
 *
 * Es el único texto del banner que exige desplegar para cambiarlo. En cuanto
 * el panel de suscripciones publique la fecha, esta constante sobra y se puede
 * borrar: el catálogo tiene prioridad.
 */
const CIERRE_CAMPANA_POR_DEFECTO = '2026-08-31';

export interface CierreCampana {
  /** Texto listo para mostrar. */
  etiqueta: string;
  /** Recta final: el banner lo pinta con más énfasis. */
  urgente: boolean;
}

/**
 * Aviso de cierre de la campaña.
 *
 * El mensaje cambia según lo que falte, porque una fecha lejana y un "último
 * día" no piden lo mismo al que lee. Lejos, la fecha ancla la decisión; cerca,
 * la cuenta atrás pesa más que el día exacto.
 *
 * Si la fecha ya pasó no se muestra nada: la promoción la apaga `descuento`,
 * no esta fecha, así que un catálogo desactualizado no debe hacernos anunciar
 * un plazo vencido.
 *
 * La fecha se parsea a mano en vez de con `new Date('YYYY-MM-DD')`, que se
 * interpreta en UTC: en Panamá (UTC-5) mostraría el día anterior.
 */
function construirCierre(fecha: string): CierreCampana | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha.trim());
  if (!m) return null;

  const [, anio, mes, dia] = m;
  const cierre = new Date(Number(anio), Number(mes) - 1, Number(dia));
  if (Number.isNaN(cierre.getTime())) return null;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const diasRestantes = Math.round((cierre.getTime() - hoy.getTime()) / 86_400_000);

  if (diasRestantes < 0) return null;
  if (diasRestantes === 0) return { etiqueta: 'Último día', urgente: true };
  if (diasRestantes === 1) return { etiqueta: 'Mañana se acaba', urgente: true };
  if (diasRestantes <= 7) return { etiqueta: `Quedan ${diasRestantes} días`, urgente: true };

  const nombreMes = cierre.toLocaleDateString('es-PA', { month: 'long' });
  return { etiqueta: `Solo hasta el ${Number(dia)} de ${nombreMes}`, urgente: false };
}

/**
 * Planes en promoción, leídos del catálogo (`limits.descuento`).
 *
 * Todo sale del catálogo: qué está en promoción, a qué precio, y contra qué
 * precio de lista se compara. Así una campaña se enciende o se apaga desde el
 * panel de suscripciones, sin desplegar el portal ni tocar textos.
 *
 * Usa el endpoint público de producto, así que no necesita sesión ni token.
 */
export function usePromoPlans() {
  const { data, isLoading } = useQuery({
    queryKey: ['subscription', 'product-info'],
    queryFn: () => subscriptionService.getProductInfo(),
    staleTime: 10 * 60 * 1000,
  });

  const planes: SubscriptionPlan[] = data?.product?.subscription_plans ?? [];

  const promos: PromoPlan[] = planes
    .filter(p => p.is_active)
    .flatMap<PromoPlan>(p => {
      // Este hook lee el catálogo directo, sin pasar por el backend: acá `price`
      // sigue siendo el precio de lista y el de campaña está en el atributo.
      const lista = Number.parseFloat(String(p.price));
      const campana = precioPromocional({ price: lista, limits: p.limits });
      if (campana === null) return [];

      return [{
        id: p.id,
        nombre: p.name,
        precio: campana,
        precioRegular: Number.isFinite(lista) ? lista : null,
        destacado: p.features?.[0] ?? null,
      }];
    })
    // Más barato primero, igual que la pieza de diseño (Emprende antes de Ilimitado).
    .sort((a, b) => a.precio - b.precio);

  // La fecha sale del catálogo si algún plan en promoción la declara; si no, del
  // valor por defecto. Se toma la primera que aparezca: una campaña tiene un
  // único cierre, aunque abarque varios planes.
  const declarada = planes
    .filter(p => p.is_active)
    .map(p => p.limits?.descuento_hasta)
    .find((f): f is string => typeof f === 'string' && f.trim() !== '');

  const cierreCampana = construirCierre(declarada ?? CIERRE_CAMPANA_POR_DEFECTO);

  return { promos, isLoading, hayPromo: promos.length > 0, cierreCampana };
}
